/**
 * Session-scoped persistence for the product-catalogue "source" filter.
 *
 * The catalogue is recreated whenever the user leaves it (e.g. opening a
 * product page) and rebuilt from scratch on return, so the selected source
 * filter used to reset to "All". Persisting it in sessionStorage keeps the
 * selection for the lifetime of the authenticated browser tab/session and is
 * cleared on logout.
 *
 * Only the source filter is persisted here — never the free-text search or any
 * other catalogue state. The storage key lives in one place so the catalogue
 * (which reads/writes it) and the logout flow (which clears it) cannot drift.
 */
export type CatalogSourceFilter = 'all' | 'iolr' | 'med';

export const CATALOG_SOURCE_FILTER_KEY = 'iolrCatalogSourceFilter';

const VALID_FILTERS: readonly CatalogSourceFilter[] = ['all', 'iolr', 'med'];

export function isCatalogSourceFilter(value: unknown): value is CatalogSourceFilter {
  return typeof value === 'string' && (VALID_FILTERS as readonly string[]).includes(value);
}

/**
 * Read the persisted source filter. Missing values yield 'all'. An invalid or
 * obsolete stored value is discarded (removed) and also falls back to 'all', so
 * a corrupted entry can never resurface. Never throws — a blocked/unavailable
 * sessionStorage (private mode, SSR) degrades to 'all'.
 */
export function readCatalogSourceFilter(): CatalogSourceFilter {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(CATALOG_SOURCE_FILTER_KEY);
  } catch {
    return 'all';
  }
  if (stored === null) {
    return 'all';
  }
  if (isCatalogSourceFilter(stored)) {
    return stored;
  }
  clearCatalogSourceFilter();
  return 'all';
}

/** Persist a valid source filter. Best-effort — silently no-ops if storage is unavailable. */
export function writeCatalogSourceFilter(value: CatalogSourceFilter): void {
  if (!isCatalogSourceFilter(value)) {
    return;
  }
  try {
    sessionStorage.setItem(CATALOG_SOURCE_FILTER_KEY, value);
  } catch {
    // sessionStorage unavailable — persistence is best-effort, so ignore.
  }
}

/** Remove the persisted source filter (called from the logout flow). */
export function clearCatalogSourceFilter(): void {
  try {
    sessionStorage.removeItem(CATALOG_SOURCE_FILTER_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
}
